import { CircularProgress, Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import FilterLine from '../../../components/filter/FilterLine';
import { useSwaggerClient } from '../../../hooks/useSwaggerClient';
import AuditlogTable from './components/AuditlogTable';

const AUDITLOG_FILTERS = [
    {
        key: 'logType',
        label: 'Log type',
        type: 'logtypeautocomplete'
    },
    {
        key: 'itemId',
        label: 'Item id',
        type: 'number'
    },
    {
        key: 'guestId',
        label: 'User',
        type: 'guestautocomplete'
    },
    {
        key: 'clientId',
        label: 'Client',
        type: 'vendorautocomplete'
    },
    {
        key: 'dateFrom',
        label: 'Date from',
        type: 'date'
    },
    {
        key: 'dateTo',
        label: 'Date to',
        type: 'date'
    }
];

function getFilterFromUrl(urlSearchPart) {
    const searchParams = new URLSearchParams(urlSearchPart.trim() !== '' ? urlSearchPart : '');

    const newClientId = parseInt(searchParams.get('clientId'), 10);
    const newGuestId = parseInt(searchParams.get('guestId'), 10);
    const logType = searchParams.get('logType');
    const itemId = searchParams.get('itemId');
    const dateFrom = searchParams.get('dateFrom');
    const dateTo = searchParams.get('dateTo');

    return {
        clientId: !Number.isNaN(newClientId) ? newClientId : '',
        guestId: !Number.isNaN(newGuestId) ? newGuestId : '',
        logType: logType || '',
        itemId: itemId || '',
        dateFrom: dateFrom || '',
        dateTo: dateTo || ''
    };
}

function getUrlFromFilter(filter) {
    const search = new URLSearchParams();
    if (filter.clientId) {
        search.append('clientId', filter.clientId);
    }
    if (filter.guestId) {
        search.append('guestId', filter.guestId);
    }
    if (filter.logType) {
        search.append('logType', filter.logType);
    }
    if (filter.itemId) {
        search.append('itemId', filter.itemId);
    }
    if (filter.dateFrom) {
        search.append('dateFrom', new Date(filter.dateFrom).toISOString().split('T')[0]);
    }
    if (filter.dateTo) {
        search.append('dateTo', new Date(filter.dateTo).toISOString().split('T')[0]);
    }

    const searchString = search.toString();
    return `?${searchString}`;
}

function AuditlogPage() {
    const history = useHistory();
    const location = useLocation();
    const [pageSize, setPageSize] = useState(25);
    const [page, setPage] = useState(0);
    const [currentFilters, setFilters] = useState(getFilterFromUrl(location.search));

    const setCurrentFilters = useCallback(
        newfilters => history.replace(getUrlFromFilter(newfilters)),
        [history]
    );

    useEffect(() => {
        setFilters(getFilterFromUrl(location.search));
    }, [location.search, setFilters]);

    const [data, loading, error] = useSwaggerClient('auditlog', 'getLogs', {
        page,
        pageSize,
        search_company_id: currentFilters.clientId || undefined,
        search_user_id: currentFilters.guestId || undefined,
        search_item_id: currentFilters.itemId || undefined,
        // search_log_type is now a multi-value (array) query param on the
        // backend; the legacy filter is single-select, so send its one value
        // wrapped in an array to serialize in the proper `?search_log_type=…`
        // format.
        search_log_type: currentFilters.logType
            ? [currentFilters.logType]
            : undefined,
        search_date_from: currentFilters.dateFrom || undefined,
        search_date_to: currentFilters.dateTo || undefined
    });

    const resetFilters = () =>
        setCurrentFilters({
            clientId: '',
            guestId: '',
            logType: '',
            itemId: '',
            dateFrom: '',
            dateTo: ''
        });

    useEffect(() => {
        if (loading) {
            return;
        }

        if (data?.items?.length === 0 && page !== 0) {
            setPage(0);
        }
    }, [page, data, loading, setPage]);

    return (
        <Grid container className="golf-reservation">
            <Grid container item xs={12}>
                <Grid item xs={12} container justifyContent="space-between">
                    <Grid item xs={10}>
                        <FilterLine
                            possibleFilters={AUDITLOG_FILTERS}
                            filters={currentFilters}
                            setFilters={setCurrentFilters}
                            resetFilters={resetFilters}
                        />
                    </Grid>
                </Grid>
                {error ? (
                    <Grid item xs={12}>
                        <Alert severity="error">
                            <h4>{error.response.body.message || error.message}</h4>
                        </Alert>
                    </Grid>
                ) : loading ? (
                    <CircularProgress />
                ) : (
                    <AuditlogTable
                        data={data}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        page={page}
                        setPage={setPage}
                    />
                )}
            </Grid>
        </Grid>
    );
}

export default AuditlogPage;
